import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useContent, DEFAULT_CONTENT } from "../context/ContentContext";
import { PerfilTab } from "../components/admin/PerfilTab";
import { FotosTab } from "../components/admin/FotosTab";
import { ProyectosTab } from "../components/admin/ProyectosTab";
import { TrayectoriaTab } from "../components/admin/TrayectoriaTab";
import { ContactoTab } from "../components/admin/ContactoTab";
import { SeguridadTab } from "../components/admin/SeguridadTab";
import { inputCls } from "../components/admin/fields";
import { toast } from "sonner";
import { Loader2, LogOut, Save } from "lucide-react";

const TABS = [
  { id: "perfil", label: "Mi perfil" },
  { id: "fotos", label: "Fotografías" },
  { id: "trayectoria", label: "Trayectoria" },
  { id: "proyectos", label: "Proyectos" },
  { id: "contacto", label: "Contacto y redes" },
  { id: "seguridad", label: "Seguridad" },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!email) return setError("Escribe tu correo para enviarte el enlace de recuperación.");
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Si el correo existe, recibirás un enlace de recuperación.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#071D4D] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl p-7 shadow-2xl space-y-4">
        <h1 className="font-serif-display text-2xl font-bold text-[#071D4D]">Portal privado</h1>
        <p className="text-sm text-neutral-500">Acceso exclusivo del administrador.</p>
        <input data-testid="admin-email-input" type="email" required placeholder="Correo electrónico"
          className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-password-input" type="password" required placeholder="Contraseña"
          className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
        <button data-testid="admin-login-button" disabled={busy}
          className="w-full rounded-full bg-[#0057FF] text-white py-2.5 text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Iniciar sesión
        </button>
        <button type="button" data-testid="admin-forgot-button" onClick={forgot}
          className="w-full text-sm text-[#0057FF]">
          ¿Olvidaste tu contraseña?
        </button>
      </form>
    </div>
  );
};

export default function AdminPage() {
  const { setContent } = useContent();
  const [user, setUser] = useState(undefined);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("perfil");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) api.get("/content").then(({ data }) => setDraft(data));
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", draft);
      setDraft(data);
      setContent({ ...DEFAULT_CONTENT, ...data });
      toast.success("Cambios guardados. Ya están visibles en la página.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === undefined)
    return (
      <div className="min-h-screen bg-[#071D4D] flex items-center justify-center">
        <Loader2 className="animate-spin text-white" size={28} />
      </div>
    );
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#0A1B4D]">
      <header className="sticky top-0 z-20 bg-[#071D4D] border-b border-[#C0C7D1]/25 px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif-display text-lg font-semibold text-white truncate">Panel de El Compa Yosi</p>
          <p className="text-xs text-[#C0C7D1] truncate">{user.email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href="/" className="text-xs text-[#C0C7D1] underline hidden sm:block" data-testid="ver-sitio-link">Ver sitio</a>
          <button data-testid="save-button" onClick={save} disabled={saving || !draft}
            className="inline-flex items-center gap-2 rounded-full bg-[#0057FF] text-white text-sm px-4 py-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar cambios
          </button>
          <button data-testid="logout-button" onClick={logout} aria-label="Cerrar sesión"
            className="rounded-full border border-[#C0C7D1]/50 text-[#C0C7D1] p-2">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <nav className="flex gap-2 overflow-x-auto px-4 py-3">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm ${
              tab === t.id ? "bg-white text-[#071D4D] font-semibold" : "text-[#C0C7D1] border border-[#C0C7D1]/40"
            }`}>
            {t.label}
          </button>
        ))}
      </nav>

      <main className="max-w-3xl mx-auto px-4 pb-16">
        {!draft ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-white" size={26} /></div>
        ) : (
          <div className="bg-white rounded-2xl p-5 sm:p-7">
            {tab === "perfil" && <PerfilTab draft={draft} setDraft={setDraft} />}
            {tab === "fotos" && <FotosTab draft={draft} setDraft={setDraft} />}
            {tab === "trayectoria" && <TrayectoriaTab draft={draft} setDraft={setDraft} />}
            {tab === "proyectos" && <ProyectosTab draft={draft} setDraft={setDraft} />}
            {tab === "contacto" && <ContactoTab draft={draft} setDraft={setDraft} />}
            {tab === "seguridad" && <SeguridadTab />}
          </div>
        )}
      </main>
    </div>
  );
}
